// src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Divider,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Google, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";
import { useGoogleLogin } from '@react-oauth/google';
import LPSLogo from '../components/LPSLogo';
import MFALogin from '../components/MFALogin';

// Custom Microsoft Icon Component
const MicrosoftIcon = ({ sx }) => (
  <svg 
    viewBox="0 0 23 23" 
    width="20" 
    height="20" 
    style={{ ...sx }}
  >
    <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
    <rect x="12" y="1" width="10" height="10" fill="#7fba00"/>
    <rect x="1" y="12" width="10" height="10" fill="#00a4ef"/>
    <rect x="12" y="12" width="10" height="10" fill="#ffb900"/>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { login, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaStatus, setCaptchaStatus] = useState({
    loading: false,
    error: false
  });
  const [showMFADialog, setShowMFADialog] = useState(false);
  const [userCredentials, setUserCredentials] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    const remembered = localStorage.getItem('rememberedUser');
    if (remembered) {
      const { email } = JSON.parse(remembered);
      setEmail(email);
      setRememberMe(true);
    }
  }, []);

  const [ssoInfo, setSsoInfo] = React.useState({ enabled: false, display_name: 'Enterprise SSO' });
  React.useEffect(() => {
    fetch('/auth/sso/config').then((r) => r.json()).then(setSsoInfo).catch(() => {});
    const m = window.location.hash.match(/sso_token=([^&]+)/);
    if (m) {
      localStorage.setItem('token', m[1]);
      window.location.hash = '';
      window.location.href = '/recordings';
    }
  }, []);

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      try {
        const result = await login('google', { 
          token: response.access_token
        });
        setSuccess('Login successful');
        navigate('/dashboard');
      } catch (error) {
        setError(error.message || 'Failed to authenticate with Google');
      }
    },
    onError: (error) => {
      setError('Failed to connect with Google');
    },
    flow: 'implicit',
    popup: true,
    ux_mode: 'popup'
  });

  const handleMicrosoftLogin = () => {
    // Microsoft 365 OAuth configuration
    const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
    const tenantId = process.env.REACT_APP_MICROSOFT_TENANT_ID || 'common';
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/microsoft/callback');
    const scopes = encodeURIComponent('openid profile email User.Read');
    
    if (!clientId) {
      setError('Microsoft login is not configured');
      return;
    }

    const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?` +
      `client_id=${clientId}&` +
      `response_type=code&` +
      `redirect_uri=${redirectUri}&` +
      `scope=${scopes}&` +
      `response_mode=query&` +
      `state=${Math.random().toString(36).substring(7)}`;

    // Open Microsoft login in a popup
    const popup = window.open(
      authUrl,
      'microsoft-login',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    // Listen for the popup to close or send a message
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        // Handle if user closed popup without completing auth
      }
    }, 1000);

    // Listen for messages from the popup
    const messageListener = (event) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'MICROSOFT_AUTH_SUCCESS') {
        clearInterval(checkClosed);
        popup.close();
        window.removeEventListener('message', messageListener);
        
        // Handle successful authentication with authorization code
        handleMicrosoftAuthSuccess(event.data.code);
      } else if (event.data.type === 'MICROSOFT_AUTH_ERROR') {
        clearInterval(checkClosed);
        popup.close();
        window.removeEventListener('message', messageListener);
        setError(event.data.error || 'Microsoft authentication failed');
      }
    };

    window.addEventListener('message', messageListener);
  };

  const handleMicrosoftAuthSuccess = async (authCode) => {
    try {
      setError(''); // Clear any previous errors
      const redirectUri = window.location.origin + '/auth/microsoft/callback';
      const result = await login('microsoft_code', { 
        code: authCode,
        redirect_uri: redirectUri 
      });
      setSuccess('Microsoft login successful');
      navigate('/dashboard');
    } catch (error) {
      console.error('Microsoft authentication error:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.detail || error.message || 'Failed to authenticate with Microsoft');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);
      const result = await login('email', formData);
      
      if (!result.success) {
        // Check if MFA is required
        if (result.error && typeof result.error === 'object' && result.error.mfa_required) {
          setUserCredentials({ email, password });
          setShowMFADialog(true);
          return;
        }
        setError(result.error);
        return;
      }
      
      setSuccess('Login successful');
      navigate('/dashboard');
    } catch (error) {
      setError('An unexpected error occurred');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (process.env.REACT_APP_RECAPTCHA_SITE_KEY && !captchaToken) {
      setError('Please complete the CAPTCHA');
      return;
    }

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setResetEmailSent(true);
      setTimeout(() => {
        setResetDialogOpen(false);
        setResetEmailSent(false);
        setResetEmail('');
      }, 3000);
    } catch (error) {
      setError('Failed to send reset email');
    }
  };

  const onCaptchaChange = (token) => {
    setCaptchaToken(token);
    setCaptchaStatus({ loading: false, error: false });
  };
  
  const onCaptchaError = () => {
    setCaptchaStatus({ loading: false, error: true });
  };

  const handleMFASuccess = (data) => {
    // AuthContext is already updated by the MFALogin component
    setShowMFADialog(false);
    setUserCredentials(null);
    setSuccess('Login successful');
    navigate('/dashboard');
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const ResetDialog = () => (
    <Dialog 
      open={resetDialogOpen} 
      onClose={() => !resetEmailSent && setResetDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Reset Password</DialogTitle>
      <form onSubmit={handleForgotPassword}>
        <DialogContent>
          {!resetEmailSent ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter your email address and we'll send you instructions to reset your password.
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
              {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                <Box sx={{ mt: 2 }}>
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={onCaptchaChange}
                    onError={onCaptchaError}
                    onExpired={() => setCaptchaToken(null)}
                    onLoadCallback={() => setCaptchaStatus({ loading: true, error: false })}
                    theme="light"
                  />

                  {captchaStatus.error && (
                    <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                      CAPTCHA failed to load. Please refresh the page.
                    </Typography>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Alert severity="success">
              Reset instructions have been sent to your email!
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!resetEmailSent && (
            <>
              <Button onClick={() => setResetDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained"
                disabled={!!(process.env.REACT_APP_RECAPTCHA_SITE_KEY && !captchaToken)}
              >
                Send Reset Link
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          p: 8,
          backgroundColor: 'background.default'
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ mb: 8 }}>
            <LPSLogo height={34} color="#142c47" />
          </Box>

          <Typography variant="h3" sx={{ 
            mb: 4,
            fontWeight: 500,
            fontSize: '2.5rem',
            lineHeight: 1.2
          }}>
            Your organization's conversations - private, on your infrastructure.
          </Typography>

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Work email"
              error={!!error}
              variant="outlined"
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              type="email"
              required
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  height: '56px'
                }
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              error={!!error}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      disabled={loading}
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  height: '56px'
                }
              }}
            />
            {error && (
            <Typography 
              color="error" 
              variant="body2" 
              sx={{ mb: 2 }}
            >
              Wrong email or password
            </Typography>
          )}
            {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
              <Box sx={{ mb: 3 }}>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={onCaptchaChange}
                  theme="light"
                />
              </Box>
            )}

            <Button 
              fullWidth 
              variant="contained" 
              color="primary"
              size="large"
              type="submit"
              disabled={loading || (process.env.REACT_APP_RECAPTCHA_SITE_KEY && !captchaToken)}
              sx={{ 
                mb: 3,
                height: '56px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '1rem'
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Continue'}
            </Button>

            {true && (<>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mb: 3
            }}>
              <Divider sx={{ flex: 1 }} />
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ px: 2 }}
              >
                or
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<Google />}
              onClick={() => { if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) { setError('Google sign-in is not configured for this deployment yet.'); return; } handleGoogleLogin(); }}
              disabled={loading}
              sx={{ 
                height: '56px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '1rem',
                border: '1px solid #e0e0e0',
                mb: 2,
                '&:hover': {
                  border: '1px solid #bdbdbd'
                }
              }}
            >
              Continue with Google
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<MicrosoftIcon />}
              onClick={handleMicrosoftLogin}
              disabled={loading}
              sx={{ 
                height: '56px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '1rem',
                border: '1px solid #e0e0e0',
                '&:hover': {
                  border: '1px solid #bdbdbd'
                }
              }}
            >
              Continue with Microsoft
            </Button>

            {ssoInfo.enabled && <Button
              fullWidth
              variant="outlined"
              onClick={() => { window.location.href = '/auth/sso/start'; }}
              disabled={loading}
              sx={{
                height: '56px', borderRadius: '8px', textTransform: 'none',
                fontSize: '1rem', border: '1px solid #e0e0e0', mt: 2,
                fontWeight: 700,
                '&:hover': { border: '1px solid #bdbdbd' }
              }}
            >
              🔐 {ssoInfo.display_name}
            </Button>}
            </>)}
          </form>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => setResetDialogOpen(true)}
              sx={{ 
                textDecoration: 'none',
                color: 'text.secondary'
              }}
            >
              Forgot password?
            </Link>
          </Box>
        </Container>
      </Box>

      <Box
        sx={{
          flex: 1,
          position: 'relative',
          background: `linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.0)), url('/images/login-background.svg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: { xs: 'none', md: 'block' }
        }}
      >
        <Box sx={{ 
          position: 'absolute',
          bottom: 40,
          width: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.0)',
        }}>
        </Box>
      </Box>
      <ResetDialog />
      <MFALogin
        open={showMFADialog}
        onClose={() => setShowMFADialog(false)}
        onSuccess={handleMFASuccess}
        userCredentials={userCredentials}
      />
    </Box>
  );
};

export default Login;